/**
 * Translation resources for react-i18next
 */

export const resources = {
  en_US: {
    translation: {
      // Common
      common: {
        back: "Back",
        next: "Next",
        loading: "Loading...",
        close: "Close",
        required: "*",
        pack: "pack",
        left: "left",
        right: "right",
        degSign: "∂",
      },

      // Header
      header: {
        back: "Back",
        close: "Close widget",
      },

      // Footer
      footer: {
        back: "Back",
        next: "Next",
        loading: "Loading...",
        addToCart: "Add to cart",
        continue: "Continue",
      },

      // Prescription Step
      prescription: {
        button: {
          manual: {
            title: "Fill it out online",
            description: "Enter your prescription manually.",
          },
          account: {
            title: "Select your saved prescription",
            description:
              "Log into your account and use your saved prescription.",
          },
        },
      },

      // Insurance
      insurance: {
        applyBenefits: "Apply vision insurance",
        benefitsApplied: "Insurance is synced",
        viewBenefits: "View benefits",
      },

      // Product Preview
      product: {
        eye: {
          right: "OD (Right Eye)",
          left: "OS (Left Eye)",
        },
      },

      // Prices Recap
      pricesRecap: {
        pricePerBox: "Price per box",
        total: "TOTAL",
        boxes: "boxes",
        included: "Included",
      },

      // Steps
      steps: {
        prescriptionType: {
          title: "Enter your prescription",
          description: "You'll need a valid prescription to place this order.",
          howToReadYourPrescription: "How to read your prescription",
          loginDescription: "Already a customer? ",
          loginButtonText: "Sign in",
        },
        advancedPrescription: {
          manualTitle: "Enter your prescription",
          manualDescription:
            "You'll need a valid prescription to place this order.",
          accountTitle: "Select your saved prescription",
          accountDescription:
            "Choose your preferred prescription and we’ll take care of the rest.",
          samePrescription: "Same prescription for both eyes",
          errors: "Please fill in missing fields",
          error: "Please fill in missing field",
          removeProduct: "Remove",
          noValue: "None",
          removeProductTitle: "Are you sure you want to remove the product?",
          removeProductDescription:
            "Doing so will remove the product and you'll go back to the listings page.",
          removeProductConfirmText: "Remove",
          removeProductCancelText: "Cancel",
        },
        addProduct: {
          title: "Need another product for your ###MISSING_EYE### eye?",
          yes: "Yes, add another product",
          no: "No, I'm all set",
          uploadedOnText: "Uploaded on ",
          add: "Add",
          addFromPrescription: "Add from prescription",
          right: "right",
          left: "left",
        },
      },

      // Modal
      modal: {
        exit: {
          title: "Are you sure you want to exit?",
          description: "Your selections will not be saved",
          cancel: "No, Continue",
          confirm: "Yes, Exit",
        },
        howToRead: {
          title: "How to read your prescription",
          description:
            "If you have a prescription for contact lenses, the prescription information will typically include the following:",
          goBack: "Go back",
          cards: [
            {
              cardTitle: "OD (Right Eye)\nOS (Left Eye)",
              attributeKey: "EYE",
              sections: [
                {
                  title: "OD (Right Eye)",
                  descriptions: [
                    'OD stands for "oculus dexter" which is Latin for "right eye".',
                  ],
                },
                {
                  title: "OS (Left Eye)",
                  descriptions: [
                    'OS stands for "oculus sinister" which is Latin for "left eye".',
                  ],
                },
              ],
            },
            {
              cardTitle: "SPH (Sphere)",
              attributeKey: "CL_SPHERE_POWER",
              sections: [
                {
                  title: "Sphere (SPH) - also known as Power (PWR)",
                  descriptions: [
                    "They mean the same thing: the strength of your prescription. If you're nearsighted you will have a minus (-) before your values, and if you're or farsighted you will have a plus (+).",
                  ],
                },
              ],
            },
            {
              cardTitle: "BC",
              attributeKey: "CL_BASE_CURVE",
              sections: [
                {
                  title: "Base Curve (BC)",
                  descriptions: [
                    "The base curve refers to the curvature of the back surface of the contact lens, measured in millimeters (mm). This measurement is tailored to match the curvature of your cornea.",
                  ],
                },
              ],
            },
            {
              cardTitle: "DIA",
              attributeKey: "CL_DIAMETER",
              sections: [
                {
                  title: "Contact lens diameter (DIA)",
                  descriptions: [
                    "The diameter measures the width of the lens from edge to edge and typically ranges between 13 and 15 mm.",
                  ],
                },
              ],
            },
            {
              cardTitle: "CYL (Cylinder)",
              attributeKey: "CL_CYLINDER",
              sections: [
                {
                  title: "Cylinder (CYL)",
                  descriptions: [
                    "This is for people with astigmatism. This is when one part of the eye needs more correction than the rest. The Cylinder value is written with a minus (-) sign.",
                  ],
                },
              ],
            },
            {
              cardTitle: "Axis",
              attributeKey: "CL_AXIS",
              sections: [
                {
                  title: "Axis",
                  descriptions: [
                    "This is only for people with astigmatism. The axis is a number between 0 and 180 and determines the orientation of the Cylinder (CYL).",
                  ],
                },
              ],
            },
            {
              cardTitle: "ADD",
              attributeKey: "CL_ADDITION",
              sections: [
                {
                  title: "Add",
                  descriptions: [
                    "This indicates the additional magnifying power that is added to the lens to correct presbyopia, a common age-related condition that affects near vision.",
                  ],
                },
              ],
            },
          ],
        },
        prescriptionDetails: {
          title: "Your prescription details",
          description:
            "We’ll use this information to show you contact lenses compatible with your prescription.",
          note: "Toric contacts are recommended",
          accordions: {
            prescriptionValues: {
              title: "Your prescription values",
            },
            doctorDetails: {
              title: "Your doctor’s details",
              doctorName: "Doctor name",
              practiceName: "Practice name",
              address: "Address",
            },
            submissionMethod: {
              title: "Your submission method",
            },
          },
        },
      },
      // My Account
      myAccount: {
        prescriptions: {
          cantFind: "Can't find it?",
          addNewPrescription: "Add new prescription",
          empty: "No saved prescriptions found",
          // Prescription Origin
          prescriptionOrigin: {
            ONLINE: "Uploaded Online",
            STORE: "Uploaded in store",
          },
          prescriptionName: "Prescription name:",
          uploadedOn: "Uploaded on ",
          expiredOn: "Expired on ",
          moreInfo: "More info",
          expirationWarning:
            "The prescription was uploaded 1 year ago. Please check if it's still valid.",
        },
      },

      // Errors
      errors: {
        genericTitle: "Something went wrong",
        genericDescription: "An unexpected error occurred. Please try again.",
        tryAgainButton: "Try again",
      },
    },
  },
};
